import React, { useEffect, useRef, useState, useCallback } from 'react';
import { initializeBlockly } from '../../../registry/blocklyInitializer';
import { convertTextToBlocks, generateFoam, blocklyXmlToAst } from '../../../registry/openfoam/openfoam.adapter';
import { registerOpenFoamBlocks } from '../../../registry/openfoam/openfoam.blocks';
import * as Blockly from 'blockly';

const OpenFoamBlocklyVisualizer = ({ dictName, fileContent, parameterGroups, onCodeChange }) => {
  const blocklyAreaRef = useRef(null);
  const blocklyDivRef = useRef(null);
  const workspace = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const resizeHandlerRef = useRef(null);
  const [fileKey, setFileKey] = useState(Date.now()); // Key to force re-initialization

  // Ensure parameterGroups is always an array
  const safeParameterGroups = Array.isArray(parameterGroups) ? parameterGroups : [];

  // Track file content changes to force re-initialization when needed
  const fileContentRef = useRef(fileContent);
  
  // Handle Blockly workspace changes
  const handleBlocklyChange = useCallback((event) => {
    // Only process events that change the workspace content
    if (!event.isUiEvent && workspace.current && onCodeChange) {
      try {
        // Get the workspace as XML
        const xml = Blockly.Xml.workspaceToDom(workspace.current);
        
        // Convert XML to AST
        const ast = blocklyXmlToAst(xml);
        
        // Generate OpenFOAM text from AST
        const generatedCode = generateFoam(ast);
        
        // Call the callback with the new code
        onCodeChange(generatedCode);
      } catch (error) {
        console.error('Error generating code from blocks:', error);
      }
    }
  }, [onCodeChange]);
  
  // Update fileKey when fileContent changes significantly
  useEffect(() => {
    // If the file content has changed significantly, force a complete re-initialization
    if (fileContentRef.current !== fileContent) {
      console.log('File content changed significantly, reinitializing workspace');
      // Clean up existing workspace
      if (workspace.current) {
        if (workspace.current.removeChangeListener) {
          workspace.current.removeChangeListener(handleBlocklyChange);
        }
        workspace.current.dispose();
        workspace.current = null;
      }
      
      // Force re-initialization by updating the key
      setFileKey(Date.now());
      fileContentRef.current = fileContent;
      setIsInitialized(false);
    }
  }, [fileContent, handleBlocklyChange]);

  // Register blocks for the dictionary
  useEffect(() => {
    if (dictName) {
      try {
        console.log(`Registering OpenFOAM blocks for dictionary: ${dictName}`);
        registerOpenFoamBlocks(dictName);
      } catch (error) {
        console.error('Error registering OpenFOAM blocks:', error);
      }
    }
  }, [dictName, fileKey]); // Re-register when dictName or fileKey changes

  // Initialize Blockly workspace
  useEffect(() => {
    if (!blocklyDivRef.current || !blocklyAreaRef.current) return;

    const initWorkspace = async () => {
      try {
        console.log(`Initializing Blockly workspace for ${dictName} with content length: ${fileContent?.length || 0}`);
        
        // Initialize the Blockly workspace
        const newWorkspace = await initializeBlockly(
          dictName,
          safeParameterGroups,
          fileContent,
          blocklyDivRef.current
        );
        
        // Store the workspace reference
        workspace.current = newWorkspace;
        
        if (workspace.current) {
          console.log('Workspace initialized successfully');
          
          // Add change listener to update code when blocks change
          workspace.current.addChangeListener(handleBlocklyChange);
          
          // Store the resize handler reference for cleanup
          if (workspace.current.resizeHandler) {
            resizeHandlerRef.current = workspace.current.resizeHandler;
          }
          
          setIsInitialized(true);
        } else {
          console.error('Failed to initialize Blockly workspace');
        }
      } catch (error) {
        console.error('Error initializing Blockly workspace:', error);
      }
    };

    console.log('Starting workspace initialization');
    initWorkspace();

    return () => {
      console.log('Cleaning up workspace');
      if (workspace.current) {
        // Remove change listener
        if (workspace.current.removeChangeListener) {
          workspace.current.removeChangeListener(handleBlocklyChange);
        }
        workspace.current.dispose();
        workspace.current = null;
      }
      
      // Remove resize event listener
      if (resizeHandlerRef.current) {
        window.removeEventListener('resize', resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
    };
  }, [dictName, safeParameterGroups, fileKey]); // Depend on fileKey instead of fileContent

  return (
    <div 
      ref={blocklyAreaRef} 
      id="blocklyArea" 
      key={fileKey} // Force re-render when fileKey changes
      style={{ 
        height: '800px', 
        width: '100%', 
        position: 'relative' 
      }}
    >
      <div 
        ref={blocklyDivRef} 
        id="blocklyDiv" 
        style={{ height: '100%', width: '100%' }}
      />
    </div>
  );
};

export default OpenFoamBlocklyVisualizer;
